import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRightIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

import {
  GetResponsibleUsersResponse,
  useDocumentTypeRecipients,
} from 'services/repositories/notificationsManagement/notificationsManagement';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { formatBasedOnSortArray } from 'support/helpers/arrays/arrays';
import { DOCUMENT_TYPE_ORDER } from 'support/helpers/const/const';
import { ConnectionsRoutes } from 'support/types';
import { DocumentType } from 'support/types/dtos';
import { BadgeList } from 'components/BadgeList/BadgeList';
import { EditRecipientsModal } from 'components/EditRecipientsModal/EditRecipientsModal';
import { Button } from 'components/Form/Button/Button';
import { NotificationRoutingBadge } from 'components/NotificationRoutingBadge/NotificationRoutingBadge';
import { Page } from 'components/Page/Page';
import { Translate } from 'components/Translate/Translate';

import { SettingsSidebar } from '../SettingsSidebar/SettingsSidebar';
import { RelationshipOverridesPanel } from './components/RelationshipOverridesPanel/RelationshipOverridesPanel';

type ResponsibleUsersEntry = GetResponsibleUsersResponse['data'][number];

const DOCUMENT_TYPE_PARAM = 'documentType';
const documentTypeValues = Object.values(DocumentType) as Array<string>;

export function NotificationsManagement() {
  const { t } = useTranslation();
  const { data: currentPartner } = useCurrentPartner();
  const { data, isLoading } = useDocumentTypeRecipients();
  const [searchParams, setSearchParams] = useSearchParams();
  const [editingDocumentType, setEditingDocumentType] = useState<DocumentType | null>(null);

  const selectedDocumentType = searchParams.get(DOCUMENT_TYPE_PARAM);
  const panelDocumentType =
    selectedDocumentType && documentTypeValues.includes(selectedDocumentType)
      ? (selectedDocumentType as DocumentType)
      : null;

  const panelEntry = panelDocumentType ? getEntryForType(data, panelDocumentType) : null;
  const panelRelationships = panelEntry?.relationships ?? [];
  const isPanelOpen = panelDocumentType !== null && panelRelationships.length > 0;
  const entries = [...(data?.data ?? [])].sort(
    formatBasedOnSortArray<DocumentType, ResponsibleUsersEntry>(DOCUMENT_TYPE_ORDER, (order, entry) =>
      order.indexOf(entry.message_type as DocumentType),
    ),
  );

  return (
    <Page isLoading={isLoading}>
      <Page.Head title={t('common:mainMenu.settings')} />

      <Page.Section leftAside={<SettingsSidebar />}>
        <div className="grid grid-cols-[320px_1fr] gap-12">
          <div className="flex flex-col gap-2">
            <h3 className="text-base font-bold text-black">{t('settings:notificationsManagement.title')}</h3>
            <p className="text-sm text-gray-500">{t('settings:notificationsManagement.subTitle')}</p>
            <p className="mt-1 text-sm text-gray-500">
              <Translate
                i18nKey="settings:notificationsManagement.connectionsHint"
                components={{
                  Link: <Link to={ConnectionsRoutes.root} className="underline hover:text-procuros-green-400" />,
                }}
              />
            </p>
            <p className="mt-1 text-sm text-gray-500">
              <Translate i18nKey="settings:notificationsManagement.docsLink" />
            </p>
          </div>

          <div className="flex w-full flex-col gap-6">
            <div className="flex-1 divide-y divide-gray-200 *:py-6 *:first:pt-0 *:last:pb-0">
              {entries.map((entry) => {
                const documentType = entry.message_type as DocumentType;
                const relationshipsCount = entry.relationships.length;

                return (
                  <div key={documentType}>
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">
                          {t(`common:messageTypes.plural.${documentType}`)}
                        </h3>
                        <div className="mt-2">
                          <BadgeList
                            values={entry.responsible.map((user) => user.email)}
                            emptyLabel={t('settings:notificationsManagement.noRecipients')}
                          />
                        </div>
                        <NotificationRoutingBadge
                          notificationResolving={entry.notification_resolving}
                          className="mt-2"
                        />
                      </div>
                      <Button
                        variant="secondary"
                        onClick={() => setEditingDocumentType(documentType)}
                        analyticsId="notifications-management:edit"
                        LeftIcon={PencilSquareIcon}
                      >
                        {t('common:edit')}
                      </Button>
                    </div>
                    {relationshipsCount > 0 && (
                      <div className="mt-2 flex items-center justify-between rounded-md bg-gray-50 px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="size-2 rounded-full bg-green-500" />
                          <span className="text-sm text-gray-600">
                            {t('settings:notificationsManagement.customRecipientsCount', {
                              count: relationshipsCount,
                            })}
                          </span>
                        </div>
                        <Button
                          variant="text"
                          onClick={() => setSearchParams({ [DOCUMENT_TYPE_PARAM]: documentType })}
                          analyticsId="notifications-management:view-details"
                          RightIcon={ChevronRightIcon}
                          className="text-gray-600! hover:text-gray-800!"
                        >
                          {t('settings:notificationsManagement.viewDetails')}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Page.Section>

      {editingDocumentType && (
        <EditRecipientsModal
          open
          onClose={() => setEditingDocumentType(null)}
          title={t('settings:notificationsManagement.modal.title', {
            documentType: t(`common:messageTypes.plural.${editingDocumentType}`),
          })}
          subtitle={t('settings:notificationsManagement.modal.subtitle', {
            documentType: t(`common:messageTypes.plural.${editingDocumentType}`),
          })}
          currentEmails={(getEntryForType(data, editingDocumentType)?.responsible ?? []).map((u) => u.email)}
          currentNotificationResolving={
            getEntryForType(data, editingDocumentType)?.notification_resolving ?? 'default_configuration'
          }
          partnerName={currentPartner?.name ?? ''}
          config={{ type: 'documentType', documentType: editingDocumentType }}
        />
      )}

      {currentPartner?.id && (
        <RelationshipOverridesPanel
          open={isPanelOpen}
          onClose={() => {
            searchParams.delete(DOCUMENT_TYPE_PARAM);
            setSearchParams(searchParams);
          }}
          documentType={panelDocumentType}
          relationships={panelRelationships}
          currentPartnerId={currentPartner.id}
        />
      )}
    </Page>
  );
}

const getEntryForType = (data: GetResponsibleUsersResponse | undefined, type: DocumentType) => {
  return data?.data?.find((entry) => entry.message_type === type);
};
